/** @prettier */

/**
 * VAT番号更新ダイアログ
 */

import {useState, useEffect} from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Stack,
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

//MTH component import
import {billing, format, pullDownMenu} from '../../define';
import {VATNumberSelectBox} from '../../component/SelectBox';
import APIErrorMessage from '../../component/APIErrorMessage';
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
import {vatNumberFormatType} from '../../Type/TypeDef';
//API import
import {
  getBillingSettingVATNumber,
  postBillingSettingVATNumber,
} from '../../API/API_Collection';
//Style import
import '../../css/common/common.css';

/**
 * VAT番号設定ダイアログ
 * @param props
 * - gwID ゲートウェイID
 * - isOpenDialog ダイアログの開閉
 * - setIsOpenDialog ダイアログの開閉セット関数
 * @returns JSX
 */
const UpdateVATNumberDialog = (props: {
  gwID: string;
  isOpenDialog: boolean;
  setIsOpenDialog: (arg0: boolean) => void;
}) => {
  /** VATの書式 */
  const [vatFormat, setVATFormat] = useState<vatNumberFormatType>('');
  const [vatNumber, setVATNumber] = useState(''); //VAT番号(文字列)
  const [okBtnDisabled, setOKBtnDisabled] = useState(false); //OKボタン非表示フラグ
  const [isOpenAPIErrorDialog, setIsOpenAPIErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [isOpenUpdateSuccessDialog, setIsOpenUpdateSuccessDialog] =
    useState(false); //VAT番号変更完了ダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  //TextBoxのエラー状態
  const [vatNumberError, setVATNumberError] = useState(false); //クーポンコード

  const trans = useTranslation().t; //翻訳関数
  const {siteID} = useParams();

  // VAT番号の取得
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (props.gwID === undefined || siteID === undefined) {
      return;
    }
    getBillingSettingVATNumber(siteID, props.gwID)
      .then(response => {
        setVATNumber(response.number);
        setVATFormat(response.format);
      })
      .catch(() => {
        setIsOpenAPIErrorDialog(true);
      });
  }, []);

  //入力項目のチェック
  useEffect(() => {
    if (vatNumber === '' || vatNumberError || apiWait) {
      setOKBtnDisabled(true);
    } else {
      setOKBtnDisabled(false);
    }
  }, [vatNumber, vatNumberError, apiWait]);

  /**
   * OKボタン押下時処理
   */
  const SettingVATNumber = () => {
    //パスパラメータを取得できない場合
    if (props.gwID === undefined || siteID === undefined) {
      return;
    }
    //VAT番号が取得できない場合
    if (vatNumber === undefined) {
      return;
    }
    //VATフォーマットが未選択の場合
    if (vatFormat === '') {
      return;
    }

    setAPIWait(true);

    // VAT番号を設定
    postBillingSettingVATNumber(siteID, props.gwID, {
      'vat-format': vatFormat,
      'vat-number': vatNumber,
    })
      .then(response => {
        // VAT番号の設定に成功した場合
        if (response.result === 'OK') {
          // 変更成功ダイアログを開く
          setIsOpenUpdateSuccessDialog(true);
          // VAT番号の設定に失敗した場合
        } else {
          alert(trans('SettingVATNumberDialog.alertMsg.vatnumber'));
        }
      })
      .catch(() => {
        setIsOpenAPIErrorDialog(true);
      })
      .finally(() => {
        setAPIWait(false);
      });
  };

  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    props.setIsOpenDialog(false);
  };

  /**
   * クーポンエラーダイアログOKボタン押下時処理
   */
  const handleUpdateSuccessClose = () => {
    // 変更成功ダイアログを閉じる
    setIsOpenUpdateSuccessDialog(false);
    // VAT番号変更ダイアログを閉じる
    props.setIsOpenDialog(false);
  };

  //パスパラメータを取得できない場合
  if (siteID === undefined) return <></>;

  return (
    <>
      <Dialog open={props.isOpenDialog} onClose={handleClose}>
        <DialogTitle>{trans('UpdateVATNumberDialog.title')}</DialogTitle>
        <DialogContent>
          {/* ダイアログに表示するメッセージ */}
          <DialogContentText>
            {trans('UpdateVATNumberDialog.description')}
          </DialogContentText>
          <Stack spacing={2}>
            <Grid container columnSpacing={1.5} rowSpacing={2}>
              <Grid
                item
                xs={12}
                className={'billingTable__data__setVATNumberDialog__subTitle'}>
                {trans('UpdateVATNumberDialog.subTitle')}
              </Grid>
              <Grid item xs={6}>
                {/* VAT番号のフォーマット */}
                <VATNumberSelectBox
                  choices={pullDownMenu.VAT_NUMBER_FORMAT}
                  value={vatFormat}
                  setValue={setVATFormat}
                  class={'billingTable__data__setVATNumberDialog__selectBox'}
                />
              </Grid>
              <Grid item xs={6}>
                {/* VAT番号 */}
                <ChkInputTextbox
                  setValue={setVATNumber}
                  setError={setVATNumberError}
                  chkLength={format.BASIC_LENGTH_MAX}
                  type="text"
                  value={vatNumber}
                  id="mv-inputApplyCouponCode"
                  class="billingTable__data__setVATNumberDialog__textField"
                  helperText=" "
                  errHelperText={trans(
                    'UpdateVATNumberDialog.txtField.errHelperTxt'
                  )}
                  variant="standard"
                  // formatに応じてプレースホルダーを変化させる
                  placeholder={billing.vatPlaceholderTable[vatFormat]}
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          {/* APPLY/CANCELボタンの設定） */}
          <SecondaryButton
            disabled={apiWait}
            onClick={handleClose}
            buttonText={trans('UpdateVATNumberDialog.buttonTxt.cancel')}
          />
          <PrimaryButton
            onClick={() => SettingVATNumber()}
            buttonText={trans('UpdateVATNumberDialog.buttonTxt.apply')}
            disabled={okBtnDisabled}
            apiWait={apiWait}
          />
        </DialogActions>
      </Dialog>
      {/* エラー表示 */}
      <APIErrorMessage
        open={isOpenAPIErrorDialog}
        setOpen={setIsOpenAPIErrorDialog}
      />
      {/* VAT番号変更成功ダイアログ */}
      <Dialog
        open={isOpenUpdateSuccessDialog}
        onClose={handleUpdateSuccessClose}
        aria-labelledby="update-vat-number-dialog-title">
        <DialogTitle id="update-vat-number-dialog-title">
          {trans('UpdateVATNumberDialog.successDialog.title')}
        </DialogTitle>
        <DialogContent>
          {/* ダイアログに表示するメッセージ */}
          <DialogContentText>
            <p>{trans('UpdateVATNumberDialog.successDialog.msg')}</p>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <PrimaryButton
            onClick={handleUpdateSuccessClose}
            buttonText={trans('UpdateVATNumberDialog.buttonTxt.ok')}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};
export default UpdateVATNumberDialog;
